/* eslint-disable prettier/prettier */
import { useSetPageTitle } from 'app/shared/context-hooks/use-titlebar.hook';
import ClassName from 'app/styles/ClassName';
import Icon from 'app/shared/components/icon/Icon';
import IconLib from 'app/shared/components/icon/icon-lib';
import Button from 'app/shared/components/button/Button';
import { PropsOf } from 'app/tools/general.types';
import clsx from 'clsx';
import React from 'react';
import { Merge } from 'type-fest';
import { getErrorMsg } from 'app/core/errors/error-utils';
import BusinessRoutePath from 'app/business-app/BusinessRoutePath';
import {
  useCurrentCompanyBankDetails,
  useDeleteCompanyBankDetails,
  useSetRepairersActivity,
} from 'app/core/api/company/api-company';
import { useEffectOnce } from 'app/tools/hooks/misc-hooks';
import { PageDetails } from 'app/core/api/company/api-company.types';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { useHistory } from 'react-router-dom';

const SectionBox: React.FC<{ title: string }> = ({
  title,
  children,
}) => (
  <>
    <h3
      className={clsx(
        'mt-1 mb-0 font-normal text-primary',
        ClassName.NO_HEIGHT_MARGIN
      )}
    >
      {title}
    </h3>
    <div className="flex flex-col rounded border border-solid border-neutral-200">
      {children}
    </div>
  </>
);

const SectionButton: React.VFC<
  Merge<
    PropsOf<typeof Button>,
    { title: string; subtitle: string; icon: IconLib }
  >
> = ({ title, subtitle, icon, ...buttonProps }) => (
  <Button variant="text" className="flex-grow h-btn" {...buttonProps}>
    <Icon {...{ icon }} />
    <div className="flex-grow ml-1">
      <h4 className="m-0 mb-0.2 text-sm font-semibold text-left line-clamp-1">
        {title}
      </h4>
      <div className="text-xs font-light text-left line-clamp-2">
        {subtitle}
      </div>
    </div>
    <Icon icon={IconLib.CHEVRON_RIGHT} />
  </Button>
);


export default function Payment(): JSX.Element {
  useSetPageTitle('Payments');
  const history = useHistory();
  // const cardId = +useUrlParams(['cardId']).id;

  const { request$: repairActivity } = useSetRepairersActivity();
  useEffectOnce(() => {
    const pageDetails: PageDetails = {
      pagename: 'Payment',
    };
    repairActivity(pageDetails).subscribe();
  });
  const { resource: company } = useCurrentCompanyBankDetails({
    fresh: true,
  });
  const { resource: bankDetails } =
    useCurrentCompanyBankDetails();
    const { request$: deleteAssignedAccount$ } =
    useDeleteCompanyBankDetails();

  const { showDialog, showSnack } = useShowInfo();

  const handleDeleteSuccess = () => {
    showSnack({
      message: 'Bank Account deleted',
      alertType: 'success',
    });
    history.replace(BusinessRoutePath.PAYMENT);
  };

  const handleDeleteError = (e: unknown) => {
    showSnack({ message: getErrorMsg(e), alertType: 'error' });
  };

  const deleteConfirmCallback = () =>
    deleteAssignedAccount$().subscribe({
      next: handleDeleteSuccess,
      error: handleDeleteError,
    });
  const handleDeleteRequest = () =>
    showDialog({
      title: 'Delete assigned product',
      message: 'Are you sure you want to delete your bank account',
      actions: [
        { text: 'Cancel' },
        {
          text: 'Confirm Delete',
          callback: () => {
            deleteConfirmCallback();
          },
        },
      ],
    });

  const CardButton: React.VFC<
    Merge<
      PropsOf<typeof Button>,
      {
        title?: string;
        subtitle: string;
        icon: IconLib;
        accountNumber?: string;
      }
    >
  > = ({ title, subtitle, icon, accountNumber, ...buttonProps }) => (
    <Button
      variant="text"
      className="flex-grow h-btn"
      {...buttonProps}
    >
      <Icon {...{ icon }} />
      <div className="flex-grow ml-1">
        <h4 className="m-0 mb-0.2 text-sm font-semibold text-left line-clamp-1">
          Bank Account ending with {title}
        </h4>
        <div className="text-xs font-light text-left line-clamp-2">
          {subtitle}
        </div>
      </div>
      {company ? (
        <Icon
        icon={IconLib.TRASH}
        onClick={handleDeleteRequest}
      />
      ) : (
        <Icon icon={IconLib.CHEVRON_RIGHT} />
      )}
      
    </Button>
  );

  return (
    <>
      <SectionBox title="Payment Options">
        {company ? (
          <CardButton
            title={bankDetails?.accountnumber}
            subtitle="See or delete bank account"
            accountNumber={bankDetails?.accountnumber}
            to={BusinessRoutePath.BANK_DETAILS}
            icon={IconLib.THREE_DOTS_BOXED}
          />
        ) : (
          <SectionButton
            title="Add Payment Method"
             subtitle="Add bank account details"
            to={BusinessRoutePath.UPDATE_BUSINESS_BANK_DETAILS}
            icon={IconLib.ADD_NEW}
          />
        )}
        <SectionButton
          title="History"
          subtitle="History of all transactions"
          to={BusinessRoutePath.HISTORY}
          icon={IconLib.PIE_CHART}
        />
      </SectionBox>
    </>
  );
}
